import flush from 'styled-jsx/server';
import React from 'react';
import { object } from 'prop-types';
import { getDataFromTree } from 'react-apollo';
import Head from 'next/head';
import { formatWrappedComponentDisplayName } from 'util/hoc';
import { getUserFromCookie } from 'util/auth';
import { getFlowFetchError, getErrorCodeFromMessage } from 'util/error';
import { getEnabledFlips } from 'util/flips';
import { parse } from 'util/json';
import initApollo from './initApollo';
import initRedux from './initRedux';

export default AppComponent =>
  class WithData extends React.Component {
    static displayName = formatWrappedComponentDisplayName(
      'withData',
      AppComponent
    );

    static propTypes = {
      // provided by getInitialProps() below
      serverState: object // eslint-disable-line
    };

    static defaultProps = {
      serverState: {
        apollo: {
          data: {}
        },
        redux: {}
      }
    };

    static async getInitialProps(args) {
      const { AppTree, ctx } = args;
      // Initial serverState with apollo (empty)
      const serverState = {
        apollo: {
          data: {}
        },
        redux: {}
      };

      // Evaluate the wrapped component's getInitialProps
      const wrappedInitialProps = AppComponent.getInitialProps
        ? await AppComponent.getInitialProps(args)
        : {};

      // Run all GraphQL queries in the component tree and extract the resulting data
      if (typeof window === 'undefined') {
        // Server ONLY
        // Extract `gen-*` headers
        const headers = ctx.req.headers
          ? Object.entries(ctx.req.headers).reduce((p, [name, value]) => {
              if (name.toLowerCase().indexOf('gen-') === 0) {
                return {
                  [name]: value,
                  ...p
                };
              }
              return p;
            }, {})
          : {};

        const redux = initRedux({
          app: {
            flips: getEnabledFlips(ctx.req.cookies),
            ssrRenderDatetime: ctx.res.get('X-SSR-Render-Datetime'),
            deployId: ctx.res.get('X-DeployID') || 'DEV',
            surrogateKey: ctx.res.get('Surrogate-Key')
          }
        });
        const apollo = initApollo(
          {},
          {
            // Callback for apollo-link-error
            onErrorHandler(error) {
              const { operation, graphQLErrors, networkError } = error;
              const flowFetchError = getFlowFetchError(error);

              if (operation.operationName === 'page') {
                // An error has occurred for a "critical" query
                // An error should be thrown if a network error, or ANY unhandled
                // GraphQL error is returned.
                // FlowFetchError is considered a valid response since it is
                // meaningful (e.g. this is a 404), and is handled separately.
                const isCriticalError = networkError || !flowFetchError;

                if (isCriticalError) {
                  // Set an error response
                  // CLEANUP: ideally we'd render an ErrorPage here, however that
                  // will require some reworking of our error handling logic.
                  const { path } = operation.variables;
                  const status = networkError ? networkError.statusCode : 500;

                  /* eslint-disable no-console */
                  console.error(
                    `GQL Error! Operation '${operation.operationName}' at '${path}' failed with status ${status}.`
                  );
                  if (networkError) {
                    console.error(`Network Error: ${networkError.message}`);
                  }
                  if (graphQLErrors) {
                    console.error('GraphQL Errors:');
                    console.error(
                      graphQLErrors.map(e => `- ${e.message}`).join('\n')
                    );
                  }
                  /* eslint-enable no-console */

                  // Always send a 5xx error so that Fastly will serve stale
                  ctx.res.sendStatus(status >= 500 ? status : 500);
                }

                // Update the response status if a FlowFetchError has occurred
                // For example, gracefully handle 404s
                if (flowFetchError) {
                  const { data = {} } = flowFetchError;
                  const { message } = data;
                  // If the error message is a number between 400-500 use it to set the status
                  const status = getErrorCodeFromMessage(message);
                  // Update status code
                  ctx.res.status(status);
                }
              }
            },
            headers
          }
        );
        try {
          // Run all GraphQL queries
          await getDataFromTree(
            <AppTree
              apollo={apollo}
              redux={redux}
              {...wrappedInitialProps}
              ctx={ctx}
            />
          );
        } catch (error) {
          // Stop errors from crashing SSR
          // Note, GraphQL errors will also be caught however these are handled
          // using apollo-link-error via the `onErrorHandler` callback (above)
          if (!error.graphQLErrors) {
            // Log other errors
            // eslint-disable-next-line no-console
            console.error(
              `Error while running "getDataFromTree" at ${ctx.req.url}`,
              error
            );
          }
        }
        // getDataFromTree does not call componentWillUnmount, so head side
        // effects need to be cleared manually
        Head.rewind();
        // Similarly flush styles (in an attempt) to prevent styled-jsx "pollution"
        // CLEANUP: Document further if this works!
        flush();
        // Extract query data from the Apollo store
        serverState.apollo.data = apollo.cache.extract();
        serverState.redux = redux.getState();
        // Get the status code after getDataFromTree has executed
        serverState.statusCode = ctx && ctx.res ? ctx.res.statusCode : 200;
      }

      return {
        serverState,
        ...wrappedInitialProps
      };
    }

    constructor(props) {
      super(props);
      this.redux = initRedux({
        ...props.serverState.redux,
        ...{
          user: typeof window !== 'undefined' ? getUserFromCookie() : null
        }
      });
      this.apollo = initApollo(props.serverState.apollo.data);
    }

    render() {
      const { serverState, ...props } = this.props;
      return (
        <AppComponent
          apollo={this.apollo}
          redux={this.redux}
          statusCode={serverState.statusCode}
          {...props}
        />
      );
    }
  };
